import { CommunityPreferenceType, ReviewAlertPreference } from 'types/communities';
import { formatCountWithUnit } from 'utils/helpers/string';

import {
  CommunityReviewAlertMode,
  CommunityReviewAlertScope,
  createCategoryAlertPreference,
  DEFAULT_ALERT_MODE_SETTINGS,
  DEFAULT_ALERT_MODE_SETTINGS_BY_SCOPE,
  REVIEW_ALERT_FILTER_FIELDS,
} from './constants';
import { CommunityReviewAlertScopeSettings } from './types';

export const hasAnyReviewAlertNotificationEnabled = (item: CommunityPreferenceType): boolean => {
  return Boolean(item.preferences?.community_review_alert_notifications || item.preferences?.competitor_review_alert_notifications);
};

export const getOverallReviewAlertPreference = (
  preferences: ReviewAlertPreference[] | undefined
): ReviewAlertPreference | undefined => {
  if (!Array.isArray(preferences)) {
    return undefined;
  }

  return preferences.find((preference) => preference.review_category_id === null);
};

export const hasAllReviewCategoriesSelected = (
  preferences: ReviewAlertPreference[] | undefined,
  allReviewCategoryIds: number[]
): boolean => {
  if (!Array.isArray(preferences) || preferences.length === 0 || allReviewCategoryIds.length === 0) {
    return false;
  }

  const selectedCategoryIds = preferences
    .map((preference) => preference.review_category_id)
    .filter((id): id is number => id !== null);

  return allReviewCategoryIds.every((id) => selectedCategoryIds.includes(id));
};

export const formatReviewCategoryCountLabel = (count: number): string => {
  return formatCountWithUnit(count, 'Category', 'Categories');
};

export const normalizePreference = (preference?: Partial<ReviewAlertPreference>): ReviewAlertPreference => {
  return {
    id: preference?.id ?? 0,
    mode: preference?.mode ?? DEFAULT_ALERT_MODE_SETTINGS.mode,
    number_of_reviews: preference?.number_of_reviews || DEFAULT_ALERT_MODE_SETTINGS.number_of_reviews,
    positive: typeof preference?.positive === 'boolean' ? preference.positive : DEFAULT_ALERT_MODE_SETTINGS.positive,
    within_months: preference?.within_months || DEFAULT_ALERT_MODE_SETTINGS.within_months,
    review_category_id: preference?.review_category_id ?? null,
  };
};

export const arePreferencesEqual = (a: ReviewAlertPreference, b: ReviewAlertPreference) => {
  return (
    REVIEW_ALERT_FILTER_FIELDS.every((field) => a[field] === b[field]) && a.review_category_id === b.review_category_id
  );
};

export const buildScopeAlertModeSettings = (preferences?: ReviewAlertPreference[]): CommunityReviewAlertScopeSettings => {
  if (!Array.isArray(preferences) || preferences.length === 0) {
    return {
      ...DEFAULT_ALERT_MODE_SETTINGS_BY_SCOPE[CommunityReviewAlertScope.COMMUNITY],
      overall: { ...DEFAULT_ALERT_MODE_SETTINGS },
      category_settings: {},
      selected_category_ids: [],
    };
  }

  const overallPreference = preferences.find((preference) => preference.review_category_id === null);
  const categoryPreferences = preferences.filter((preference) => preference.review_category_id !== null);
  const sourcePreference = overallPreference ?? categoryPreferences[0];
  const categorySettings = categoryPreferences.reduce<Record<number, ReviewAlertPreference>>((acc, preference) => {
    if (preference.review_category_id !== null) {
      acc[preference.review_category_id] = normalizePreference({
        ...preference,
        review_category_id: preference.review_category_id,
      });
    }

    return acc;
  }, {});

  return {
    mode: overallPreference ? CommunityReviewAlertMode.OVERALL : CommunityReviewAlertMode.CATEGORY,
    overall: normalizePreference({
      ...(sourcePreference ?? DEFAULT_ALERT_MODE_SETTINGS),
      mode: CommunityReviewAlertMode.OVERALL,
      review_category_id: null,
    }),
    category_settings: categorySettings,
    selected_category_ids: categoryPreferences
      .map((preference) => preference.review_category_id)
      .filter((id): id is number => id !== null),
  };
};

export const areScopeSettingsEqual = (
  current: CommunityReviewAlertScopeSettings,
  initial: CommunityReviewAlertScopeSettings
) => {
  if (current.mode !== initial.mode) {
    return false;
  }

  if (!arePreferencesEqual(current.overall, initial.overall)) {
    return false;
  }

  const currentIds = [...current.selected_category_ids].sort((a, b) => a - b);
  const initialIds = [...initial.selected_category_ids].sort((a, b) => a - b);

  if (currentIds.length !== initialIds.length || currentIds.some((id, index) => id !== initialIds[index])) {
    return false;
  }

  const allCategoryIds = Array.from(
    new Set([...Object.keys(current.category_settings), ...Object.keys(initial.category_settings)])
  ).map(Number);

  return allCategoryIds.every((categoryId) => {
    const currentPreference = current.category_settings[categoryId] ?? createCategoryAlertPreference(categoryId);
    const initialPreference = initial.category_settings[categoryId] ?? createCategoryAlertPreference(categoryId);

    return arePreferencesEqual(currentPreference, initialPreference);
  });
};
